import { datadogLogs } from '@datadog/browser-logs'
import { Text, TextHighlight, TextWeight } from '@focaldata/cin-ui-components'
import checkSrc from 'assets/check.svg'
import { AppState, AppStateContext } from 'containers/State/AppState'
import useGetRedirectUrl from 'hooks/useGetRedirectUrl'
import usePerformQualityCheck, {
  CheckResult,
  QualityCheckResult
} from 'hooks/usePerformQualityCheck'
import { useQuestionnaireParams } from 'hooks/useQuestionnaireParams'
import useUpdateRespondent from 'hooks/useUpdateRespondent'
import InfoPageLayout from 'layouts/InfoPageLayout/InfoPageLayout'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { shouldUseNewApiEndpoints } from 'utils/featureFlags'
import { buildAndSignPureSpectrumUrl } from 'utils/generatePureSpectrumHash'
import {
  getCintExchangeRedirectionURL,
  getPureSpectrumStatusCode,
  PanelSupplierCode
} from '../../model/common'
import { RespondentProgress } from '../../model/respondentProgress'
import { RespondentEvent } from '../../model/respondentStatus'

const COUNTDOWN_SECONDS = 3

const determineRespondentStatus = (
  currentProgress: RespondentProgress
): RespondentEvent => {
  // Priority order matches old getRespondentStatus logic
  if (currentProgress.isCompleted && !currentProgress.isQualityTerminated)
    return RespondentEvent.Complete
  if (currentProgress.isQualityTerminated)
    return RespondentEvent.QualityTerminate
  if (currentProgress.isQuotaFull) return RespondentEvent.QuotaFull
  if (currentProgress.isScreened) return RespondentEvent.ScreenOut
  return RespondentEvent.NotFound
}

const CompletePage: React.FC = () => {
  const mandatoryQueryParams = useQuestionnaireParams()
  const isCustom = mandatoryQueryParams?.panelSupplier === PanelSupplierCode.Fd
  const panelSupplierCode = mandatoryQueryParams?.panelSupplier

  const [displayTimer, setDisplayTimer] = useState<number | null>(null)
  // Integrated suppliers always redirect at the end of the survey; custom
  // audiences only redirect when the backend resolver returns a URL (resolved
  // asynchronously below). Drives the end-screen copy and countdown.
  const [willRedirectToSupplier, setWillRedirectToSupplier] = useState(
    !isCustom
  )

  const {
    respondentProgress: [progress, setProgress],
    responseChoicePromises,
    renderedQuestionnaire: [data]
  } = useContext<AppState>(AppStateContext)
  const updateRespondent = useUpdateRespondent()
  const performQualityCheck = usePerformQualityCheck()
  const getRedirectUrl = useGetRedirectUrl()
  const submissionStartedRef = useRef(false)

  // Custom-audience respondents are redirected to the supplier's own end-link
  // only when both:
  //  - the new fd-core-respondent PATCH path was used — the resolver reads the
  //    primary DB that only that path populates, and
  //  - the RID came from the supplier via the URL — an app-generated RID
  //    (ps=fd with no `rid`) is not a real supplier RID and must never be sent
  //    back to the supplier.
  // Preview is already excluded by the early return in the submission effect.
  const usedNewApi = mandatoryQueryParams?.respondentId
    ? shouldUseNewApiEndpoints(mandatoryQueryParams.respondentId)
    : false
  const ridFromUrl = !mandatoryQueryParams?.respondentIdAutoGenerated
  const canResolveCustomRedirect = isCustom && usedNewApi && ridFromUrl

  const redirectionUrl = (
    respondentPanelSupplierCode: string,
    respondentProgress: RespondentProgress | undefined
  ): string => {
    switch (respondentPanelSupplierCode) {
      case PanelSupplierCode.Cint:
        return `${process.env.REACT_APP_CINT_REDIRECT_URL}/${respondentProgress?.respondentId}`
      case PanelSupplierCode.Prolific:
        return `${process.env.REACT_APP_PROLIFIC_REDIRECT_URL}?cc=${
          respondentProgress?.isCompleted
            ? process.env.REACT_APP_PROLIFIC_COMPLETE_CODE
            : process.env.REACT_APP_PROLIFIC_SCREEN_OUT_CODE
        }`
      case PanelSupplierCode.PureSpectrum: {
        const fullUrl = `${
          process.env.REACT_APP_PURESPECTRUM_REDIRECT_URL
        }?st=${getPureSpectrumStatusCode(respondentProgress)}&transaction_id=${
          respondentProgress?.respondentId
        }`

        if (!process.env.REACT_APP_PURESPECTRUM_URL_SECRET_KEY) {
          return fullUrl
        }

        return buildAndSignPureSpectrumUrl(
          fullUrl,
          process.env.REACT_APP_PURESPECTRUM_URL_SECRET_KEY
        )
      }
      case PanelSupplierCode.CintExchange:
        return getCintExchangeRedirectionURL(respondentProgress)
      default:
        return window.location.href
    }
  }

  const isCompleted = progress?.isCompleted

  // Imperative submission sequence - runs once on mount
  useEffect(() => {
    // Skip if in preview mode
    if (mandatoryQueryParams?.preview) return

    // Prevent double execution (React.StrictMode can cause this in development)
    if (submissionStartedRef.current) return
    submissionStartedRef.current = true

    const checkAllResponseChoicesHaveBeenSent = async (): Promise<boolean> => {
      if (responseChoicePromises.length === 0) {
        return true
      }

      datadogLogs.logger.info('Waiting for response choices to complete', {
        pendingCount: responseChoicePromises.length
      })

      const results = await Promise.allSettled(responseChoicePromises)
      const failedCount = results.filter((r) => r.status === 'rejected').length
      const hasFailures = failedCount > 0

      datadogLogs.logger.info('All response choices settled', {
        total: results.length,
        failed: failedCount,
        succeeded: results.length - failedCount
      })

      if (hasFailures) {
        datadogLogs.logger.warn('Some response choices failed', {
          failedCount,
          totalCount: results.length
        })
      }

      return !hasFailures
    }

    const performQualityCheckAndHandleResult = async (): Promise<{
      qualityCheckResult: QualityCheckResult
      shouldQualityTerminate: boolean
    }> => {
      datadogLogs.logger.info('Performing quality check for completed survey')
      const qualityCheckResult = await performQualityCheck()

      const useQualityCheckForProgress =
        process.env.REACT_APP_USE_QUALITY_CHECK_FOR_PROGRESS === 'true'

      const shouldQualityTerminate =
        qualityCheckResult.checkResult === CheckResult.Failed &&
        useQualityCheckForProgress

      if (shouldQualityTerminate) {
        datadogLogs.logger.warn('Quality check failed')
      }

      datadogLogs.logger.info('Quality check result', {
        checkResult: qualityCheckResult.checkResult,
        useQualityCheckForProgress
      })

      return { qualityCheckResult, shouldQualityTerminate }
    }

    const redirectTo = (
      redirectUrl: string,
      finalProgress: RespondentProgress
    ) => {
      datadogLogs.logger.info('Redirecting respondent to panel', {
        redirectUrl,
        panelSupplierCode,
        surveyId: finalProgress?.surveyId,
        respondentId: finalProgress?.respondentId,
        isCompleted: finalProgress?.isCompleted,
        isQualityTerminated: finalProgress?.isQualityTerminated,
        isScreened: finalProgress?.isScreened,
        isQuotaFull: finalProgress?.isQuotaFull
      })
      window.location.assign(redirectUrl)
    }

    const startCountdownAndRedirect = (
      redirectUrl: string,
      finalProgress: RespondentProgress
    ) => {
      let countdown = COUNTDOWN_SECONDS

      const updateCountdown = () => {
        setDisplayTimer(countdown)
        countdown -= 1

        if (countdown >= 0) {
          setTimeout(updateCountdown, 1000)
        } else {
          redirectTo(redirectUrl, finalProgress)
        }
      }

      updateCountdown()
    }

    // Custom audiences: resolve the supplier end-link server-side (the backend
    // maps the recorded terminal status to a URL) and, if one is configured,
    // show the countdown then redirect. A null result keeps the close-page
    // screen — it never blocks the end screen.
    const resolveAndRedirectCustom = async (
      finalProgress: RespondentProgress
    ) => {
      const resolvedUrl = await getRedirectUrl()

      if (!resolvedUrl) {
        return
      }

      setWillRedirectToSupplier(true)
      startCountdownAndRedirect(resolvedUrl, finalProgress)
    }

    const runCompleteSubmissionSequence = async () => {
      try {
        if (!progress) {
          datadogLogs.logger.error(
            'Cannot run submission sequence: progress is missing'
          )
          return
        }

        // Build current progress state based on checks
        const currentProgress: RespondentProgress = { ...progress }

        // Check 1: Wait for all response choices to complete
        const responseChoicesSucceeded =
          await checkAllResponseChoicesHaveBeenSent()

        if (!responseChoicesSucceeded) {
          currentProgress.isQualityTerminated = true
          currentProgress.isCompleted = false
        }

        // Check 2: Run quality check only for respondents who successfully completed
        // Skip for disqualified respondents or returning respondents with existing status
        const isDisqualified =
          currentProgress.isQuotaFull ||
          currentProgress.isScreened ||
          currentProgress.isQualityTerminated ||
          data?.isSurveyFull

        const shouldRunQualityCheck =
          responseChoicesSucceeded &&
          currentProgress.isCompleted &&
          !currentProgress.hasExistingStatus &&
          !isDisqualified

        let qualityCheckResult: QualityCheckResult

        if (shouldRunQualityCheck) {
          const result = await performQualityCheckAndHandleResult()
          qualityCheckResult = result.qualityCheckResult

          if (result.shouldQualityTerminate) {
            currentProgress.isQualityTerminated = true
            currentProgress.isCompleted = false
          }
        } else {
          datadogLogs.logger.info(
            'Skipping quality check for disqualified respondent',
            {
              isCompleted: currentProgress.isCompleted,
              isQuotaFull: currentProgress.isQuotaFull,
              isScreened: currentProgress.isScreened,
              isQualityTerminated: currentProgress.isQualityTerminated,
              hasExistingStatus: currentProgress.hasExistingStatus,
              isSurveyFull: data?.isSurveyFull,
              responseChoicesFailed: !responseChoicesSucceeded
            }
          )
          qualityCheckResult = {
            surveyId: progress.surveyId,
            supplierRespondentId: progress.respondentId,
            checkResult: CheckResult.Skipped
          }
        }

        // Update React state if quality terminated
        if (
          currentProgress.isQualityTerminated &&
          !progress.isQualityTerminated
        ) {
          setProgress({
            ...currentProgress
          })
        }

        // Determine and send status
        const respondentStatus = determineRespondentStatus(currentProgress)
        datadogLogs.logger.info('Sending respondent status', {
          respondentStatus
        })
        const { blocked } = await updateRespondent(
          respondentStatus,
          qualityCheckResult
        )

        // If backend rejected with 400 (survey completed / terminal status),
        // treat like isSurveyFull: redirect as quota-full, not as completed
        if (blocked) {
          currentProgress.isCompleted = false
          currentProgress.isQuotaFull = true
          setProgress({ ...currentProgress })
        }

        // Step 4: Redirect
        // Re-determine status after potential blocked update
        const finalStatus = blocked
          ? determineRespondentStatus(currentProgress)
          : respondentStatus
        datadogLogs.logger.info(
          'Respondent status sent successfully, redirecting'
        )
        if (isCustom) {
          // Custom audiences: redirect to the supplier end-link resolved from
          // the recorded status. Skipped when the RID is app-generated or the
          // legacy API path was used (the resolver's DB isn't populated then),
          // in which case the close-page screen is kept.
          if (canResolveCustomRedirect) {
            await resolveAndRedirectCustom(currentProgress)
          }
        } else if (finalStatus === RespondentEvent.Complete) {
          startCountdownAndRedirect(
            redirectionUrl(panelSupplierCode || '', currentProgress),
            currentProgress
          )
        } else {
          redirectTo(
            redirectionUrl(panelSupplierCode || '', currentProgress),
            currentProgress
          )
        }
      } catch (error) {
        const isCintExchange =
          panelSupplierCode === PanelSupplierCode.CintExchange

        datadogLogs.logger.error(
          isCintExchange
            ? 'Failed to send respondent status'
            : 'Failed to complete status submission',
          { error }
        )

        if (isCintExchange) {
          throw new Error('Failed to send respondent status')
        } else if (isCustom) {
          // Backend didn't confirm the update. Ask the resolver for the URL
          // based on whatever status is recorded so we never over-credit a
          // failed complete (the backend resolves from the stored status).
          if (canResolveCustomRedirect && progress) {
            await resolveAndRedirectCustom(progress)
          }
        } else if (progress) {
          // Backend didn't confirm completion — don't redirect as complete
          // to avoid paying panel for unrecorded completes
          const failedProgress: RespondentProgress = {
            ...progress,
            isCompleted: false,
            isQuotaFull: true
          }
          setProgress(failedProgress)
          redirectTo(
            redirectionUrl(panelSupplierCode || '', failedProgress),
            failedProgress
          )
        }
      }
    }

    // Start the entire sequence
    runCompleteSubmissionSequence()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array - run once on mount only

  const getSubtitle = () => {
    // Custom audiences with no supplier redirect see a close-page screen;
    // everyone who will be redirected (integrated suppliers, or custom
    // audiences with a resolved end-link) sees the standard end-of-survey copy.
    if (progress?.isQualityTerminated) {
      return willRedirectToSupplier
        ? 'completePage_unfortunately'
        : 'completePage_unfortunately_close_page'
    }

    if (isCompleted)
      return willRedirectToSupplier
        ? 'completePage_weAppreciate'
        : 'completePage_closeWindow'
    return willRedirectToSupplier
      ? 'completePage_unfortunately'
      : 'completePage_unfortunately_close_page'
  }

  const locIds = new Map<string, string>([
    [
      'title',
      isCompleted && !progress?.isQualityTerminated
        ? 'completePage_thanks'
        : 'completePage_thanks2'
    ],
    ['subtitle', getSubtitle()],
    [
      'action',
      mandatoryQueryParams?.preview
        ? 'completePage_preview'
        : 'completePage_redirecting'
    ]
  ])

  return (
    <InfoPageLayout
      visual={<img src={checkSrc} alt="check icon" />}
      title={
        <FormattedMessage
          id={locIds.get('title')}
          defaultMessage="Thanks for completing our survey!"
        />
      }
      subtitle={
        <FormattedMessage
          id={locIds.get('subtitle')}
          defaultMessage={
            willRedirectToSupplier
              ? 'We appreciate you taking the time to answer the survey. To officially complete this survey, wait until you are redirected.'
              : 'You may now close this page.'
          }
        />
      }
      action={
        willRedirectToSupplier && displayTimer !== null ? (
          <Text highlight={TextHighlight.Emphasis} weight={TextWeight.SemiBold}>
            <FormattedMessage
              id={locIds.get('action')}
              defaultMessage="Redirecting to {panel} in {count} seconds"
              values={{ count: displayTimer, panel: panelSupplierCode }}
            />
          </Text>
        ) : undefined
      }
    />
  )
}

export default CompletePage
